import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Input, Button, Spin } from 'antd';
import { PaperPlaneTilt, PencilSimple } from '@phosphor-icons/react';
import { captureAnalytics } from '../../actions/preload';
import useReferralMessages from '../../hooks/useReferralMessages';
import useSingleFlight from '../../hooks/useSingleFlight';
import './referralMessages.scss';

const { TextArea } = Input;

// Mirrors ReferralMessageEditor::EDIT_WINDOW; the server makes the final call.
const EDIT_WINDOW_MS = 15 * 60 * 1000;

const ReferralMessages = ({ slug, archived, cable, user, loggedIn, onUnread }) => {
  const [messageContent, setMessageContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  // Re-evaluated periodically so Edit disappears once a message's window closes.
  const [now, setNow] = useState(() => Date.now());
  const currentSlugRef = useRef(slug);
  useEffect(() => () => {
    currentSlugRef.current = null;
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  const {
    messages,
    sendMessage,
    editMessage,
    canSend,
    markAsRead,
    loading,
    referrerOrg,
    partnerOrg,
    showStartMessage,
    hasUnread,
    setActive,
    messagesEndRef,
    sendError,
    historyError,
    retryHistory,
    shareRevoked,
  } = useReferralMessages(slug, cable, user, loggedIn);

  useEffect(() => {
    setActive(true);
    return () => setActive(false);
  }, [setActive]);

  // Notify parent of unread state changes
  useEffect(() => {
    if (onUnread) onUnread(hasUnread);
  }, [hasUnread, onUnread]);

  // Mark last message as read when messages tab is active
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.message_id && lastMsg.sender_email !== user?.uid) {
        markAsRead(lastMsg.message_id);
      }
    }
  }, [messages, markAsRead, user]);

  const inputDisabled = archived || shareRevoked || !canSend;

  // Single-flight guard for same-task cable sends.
  const guardedSend = useSingleFlight((content) => sendMessage(content));

  const handleSend = async () => {
    if (!messageContent.trim() || inputDisabled) return;
    const submitted = messageContent;
    const sent = await guardedSend(submitted);
    if (sent && currentSlugRef.current === slug) {
      setMessageContent((current) => (current === submitted ? '' : current));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const editingDisabled = archived || shareRevoked;
  // The transcript memo below renders these, so they are stable callbacks listed in its deps.
  const startEdit = useCallback((msg) => {
    setEditingId(msg.message_id);
    setEditDraft(msg.content);
  }, []);
  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditDraft('');
  }, []);
  const saveEdit = useCallback(async () => {
    const messageId = editingId;
    if (!messageId || !editDraft.trim() || savingEdit) return;
    setSavingEdit(true);
    let saved = false;
    try {
      saved = await editMessage(messageId, editDraft);
    } catch {
      // editMessage reports its own errors; treat anything unexpected as a failed save.
    } finally {
      if (currentSlugRef.current === slug) setSavingEdit(false);
    }
    // On failure the editor stays open with the draft; the bubble still shows the saved text.
    if (saved && currentSlugRef.current === slug) {
      setEditingId((current) => (current === messageId ? null : current));
    }
  }, [editingId, editDraft, savingEdit, editMessage, slug]);
  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  }, [saveEdit, cancelEdit]);

  // Typing changes only the composer. Reuse the transcript until its data or
  // viewer changes, while still respecting system timezone changes on render.
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentUserUid = user?.uid;
  const renderedMessages = useMemo(() => {
    const formatTimestamp = (timestamp, type) => {
      const dateTime = new Date(timestamp);
      if (type === 'date') {
        return dateTime.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: currentTimeZone,
        });
      }
      return dateTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: currentTimeZone,
      });
    };

    return messages.map((msg, index) => {
      const isSender = msg.sender_email === currentUserUid;
      const isEditing = editingId === msg.message_id;
      // Messages carried over from a merged referral keep their source slug and stay
      // read-only (the server only edits messages stored under this referral).
      const canEdit = isSender && !editingDisabled && Boolean(msg.message_id) && msg.slug === slug
        && now - new Date(msg.timestamp).getTime() < EDIT_WINDOW_MS;
      const draftChanged = editDraft.trim() && editDraft.trim() !== msg.content;
      const showDate = index === 0 ||
        formatTimestamp(msg.timestamp, 'date') !== formatTimestamp(messages[index - 1].timestamp, 'date');
      const showName = index === 0 || msg.sender_name !== messages[index - 1].sender_name;

      return (
        <div key={msg.message_id || index}>
          {showDate && (
            <div className="referral-messages__datestamp">
              {formatTimestamp(msg.timestamp, 'date')}
            </div>
          )}
          {showName && (
            <div className={`referral-messages__sender-name ${isSender ? 'referral-messages__sender-name--right' : ''}`}>
              {msg.sender_name}
            </div>
          )}
          <div className={`referral-messages__message ${isSender ? 'referral-messages__message--sent' : ''}`}>
            {isEditing ? (
              <div className="referral-messages__edit">
                <TextArea
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  autoFocus
                  // Locked while saving so a successful save can't discard text typed after Save.
                  disabled={savingEdit}
                  className="referral-messages__edit-textarea"
                />
                <div className="referral-messages__edit-actions">
                  <Button
                    size="small"
                    onClick={cancelEdit}
                    disabled={savingEdit}
                    className="referral-messages__edit-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={saveEdit}
                    loading={savingEdit}
                    disabled={!draftChanged}
                    className="referral-messages__edit-save"
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className={`referral-messages__bubble ${isSender ? 'referral-messages__bubble--sent' : 'referral-messages__bubble--received'}`}>
                <p className="referral-messages__bubble-text">{msg.content}</p>
                <span className="referral-messages__bubble-time">
                  {formatTimestamp(msg.timestamp, 'time')}
                  {msg.edited_at && ' · Edited'}
                </span>
              </div>
            )}
            {canEdit && !isEditing && (
              <Button
                type="text"
                size="small"
                onClick={() => startEdit(msg)}
                className="referral-messages__edit-trigger"
                icon={<PencilSimple size={12} />}
                aria-label="Edit message"
              >
                Edit
              </Button>
            )}
          </div>
          {msg.last_read_by && index === messages.length - 1 && isSender && (
            <div className="referral-messages__read-receipt">
              Last viewed by {msg.last_read_by}
            </div>
          )}
        </div>
      );
    });
  }, [
    messages, currentUserUid, currentTimeZone, editingId, editDraft, savingEdit, editingDisabled, now,
    startEdit, cancelEdit, saveEdit, handleEditKeyDown, slug,
  ]);

  if (loading) {
    return (
      <div className="referral-messages__loading">
        <Spin size="large" />
      </div>
    );
  }

  const otherOrg = user?.organization === referrerOrg ? partnerOrg : referrerOrg;

  return (
    <div className="referral-messages">
      <div className={`referral-messages__list ${showStartMessage ? 'referral-messages__list--empty' : ''}`}>
        {showStartMessage && !historyError ? (
          <div className="referral-messages__start">
            <p>Start a conversation{otherOrg ? ` with ${otherOrg}` : ''}.</p>
          </div>
        ) : (
          renderedMessages
        )}
        <div ref={messagesEndRef} />
      </div>
      {historyError && (
        <div role="alert" className="referral-messages__send-error">
          {historyError}
          <Button
            onClick={() => {
              captureAnalytics('referral_chat_history_retry_clicked');
              retryHistory();
            }}
          >
            Retry
          </Button>
        </div>
      )}
      {sendError && (
        <div className="referral-messages__send-error">{sendError}</div>
      )}
      <div className="referral-messages__input-area">
        {archived || shareRevoked ? (
          <div className="referral-messages__archived-notice">
            {shareRevoked
              ? 'Share has been revoked. Messaging is disabled.'
              : 'This referral is archived. Messaging is disabled.'}
          </div>
        ) : (
          <>
            <TextArea
              placeholder="Write a message..."
              disabled={inputDisabled}
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              onKeyDown={handleKeyDown}
              autoSize={{ minRows: 1, maxRows: 4 }}
              className="referral-messages__textarea"
            />
            <Button
              type="primary"
              onClick={handleSend}
              disabled={inputDisabled || !messageContent.trim()}
              className="referral-messages__send-btn"
              icon={<PaperPlaneTilt size={18} weight="fill" />}
            />
          </>
        )}
      </div>
    </div>
  );
};

ReferralMessages.propTypes = {
  slug: PropTypes.string,
  archived: PropTypes.bool,
  cable: PropTypes.object,
  user: PropTypes.object,
  loggedIn: PropTypes.bool,
  onUnread: PropTypes.func,
};

ReferralMessages.defaultProps = {
  slug: null,
  archived: false,
  cable: null,
  user: null,
  loggedIn: false,
  onUnread: null,
};

export default ReferralMessages;
